import React, { useState, useMemo } from "react";
import {
  Building2,
  User,
  CheckCircle2,
  XCircle,
  MapPin,
  ChevronRight,
  X,
  MessageSquare,
  CreditCard,
  Calendar,
} from "lucide-react";

import {
  usePayouts,
  usePayout,
  usePayPayout,
  useRejectPayout,
} from "../../Hooks/usePayouts";
import toast from "react-hot-toast";
import { canAccess } from "../../utils/permissions";

export default function SurveyorsCashRequests() {
  const { data: payouts = [], isLoading } = usePayouts();
  const [selectedId, setSelectedId] = useState(null);
  const [note, setNote] = useState("");

  const { data: selectedPayout } = usePayout(selectedId);

  const payMutation = usePayPayout();
  const rejectMutation = useRejectPayout();

  const canRejectSurveyorCash = canAccess("reject_surveyor_cash");
  const canAcceptSurveyorCash = canAccess("accept_surveyor_cash");

  const pendingTotal = payouts
    .filter((p) => p.status === "pending_admin")
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const paidCount = payouts.filter((p) => p.status === "completed").length;

  const paidTotal = payouts
    .filter((p) => p.status === "completed")
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const handlePay = () => {
    if (!canAcceptSurveyorCash) {
      toast.error("You don't have permission");
      return;
    }
    payMutation.mutate(selectedId, {
      onSuccess: () => setSelectedId(null),
    });
  };

  const handleReject = () => {
    if (!canRejectSurveyorCash) {
      toast.error("You don't have permission");
      return;
    }
    if (!note.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    rejectMutation.mutate(
      { id: selectedId, reason: note },
      {
        onSuccess: () => {
          setSelectedId(null);
          setNote("");
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading payouts...</div>
      </div>
    );
  }

  if (!canAccess("view_surveyor_cash")) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm font-medium">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <CreditCard className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
        <p className="text-gray-500 max-w-md font-medium">
          You don't have the required permissions to view surveyor cash requests.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen">
        <div className="bg-white border-b border-gray-200">
          <div className="p-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Payout Requests
            </h1>
            <p className="text-base text-gray-600 mt-1">
              Review and process surveyor payout requests
            </p>
          </div>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl p-6 text-white shadow-lg">
              <p className="text-red-100 text-sm font-medium">Pending Amount</p>
              <p className="text-4xl font-bold mt-2">
                USD {pendingTotal.toLocaleString()}
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
              <p className="text-emerald-100 text-sm font-medium">
                Paid Requests
              </p>
              <p className="text-4xl font-bold mt-2">{paidCount}</p>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
              <p className="text-blue-100 text-sm font-medium">Total Paid</p>
              <p className="text-4xl font-bold mt-2">
                USD {paidTotal.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="space-y-5">
            {payouts.map((p) => (
              <div
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className="bg-white rounded-2xl shadow hover:shadow-lg transition cursor-pointer border border-gray-200 hover:border-emerald-400 group"
              >
                <div className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                      {p.surveyor?.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {p.surveyor?.full_name}
                      </h3>
                      <p className="text-sm text-gray-600">{p.company.name}</p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-3xl font-bold text-emerald-600">
                      USD {parseFloat(p.amount).toLocaleString()}
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 ${p.status === "pending_admin"
                          ? "bg-amber-100 text-amber-700"
                          : p.status === "completed"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                          }`}
                      >
                        {p.status === "completed" && (
                          <CheckCircle2 className="w-4 h-4" />
                        )}
                        {p.status === "rejected" && (
                          <XCircle className="w-4 h-4" />
                        )}
                        {p.status.replace("_", " ").charAt(0).toUpperCase() +
                          p.status.replace("_", " ").slice(1)}
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedPayout && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedId(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">
                    Payout Request #{selectedPayout.id}
                  </h2>
                  <p className="text-emerald-100">
                    Surveyor: {selectedPayout.surveyor.full_name}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedId(null)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-8 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div className="text-center py-8 bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl border-2 border-emerald-200">
                <p className="text-gray-600 text-sm">Amount</p>
                <p className="text-5xl font-bold text-emerald-700 mt-2">
                  USD {parseFloat(selectedPayout.amount).toLocaleString()}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-2xl p-6 border">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-emerald-600" /> Surveyor
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                        {selectedPayout.surveyor.full_name
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold">
                          {selectedPayout.surveyor.full_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {selectedPayout.surveyor.phone}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-600" /> Company
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="font-bold text-gray-900">
                      {selectedPayout.company.name}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {selectedPayout.surveyor.location || "Not specified"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-600" /> Bank
                    Details
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="font-medium">Holder:</span>{" "}
                      {selectedPayout.bank_details.account_holder}
                    </p>
                    <p>
                      <span className="font-medium">Bank:</span>{" "}
                      {selectedPayout.bank_details.bank_name}
                    </p>
                    <p>
                      <span className="font-medium">IBAN:</span>{" "}
                      {selectedPayout.bank_details.iban}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-600" /> Request
                    Info
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="font-medium">Requested:</span>{" "}
                      {new Date(selectedPayout.created_at).toLocaleDateString()}
                    </p>
                    <p>
                      <span className="font-medium">Jobs:</span>{" "}
                      {selectedPayout.jobs?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              {selectedPayout.status === "pending_admin" && (
                <>
                  <div>
                    <label className="flex items-center gap-2 font-medium mb-2">
                      <MessageSquare className="w-5 h-5 text-emerald-600" />
                      Rejection Reason (optional for approve)
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm resize-none"
                      placeholder="Add note..."
                    />
                  </div>

                  <div className="flex justify-end gap-4 pt-6 border-t-2">
                    {canRejectSurveyorCash && (
                      <button
                        onClick={handleReject}
                        disabled={rejectMutation.isPending}
                        className="px-8 py-3.5 border-2 border-red-500 text-red-600 rounded-xl font-medium hover:bg-red-50 transition"
                      >
                        Reject
                      </button>
                    )}
                    {canAcceptSurveyorCash && (
                      <button
                        onClick={handlePay}
                        disabled={payMutation.isPending}
                        className="px-10 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition flex items-center gap-3"
                      >
                        <CheckCircle2 className="w-6 h-6" />
                        Mark as Paid
                      </button>
                    )}
                  </div>
                </>
              )}

              {selectedPayout.status === "completed" && (
                <div className="text-center text-lg font-medium text-emerald-600">
                  This payout has been successfully paid
                </div>
              )}

              {selectedPayout.status === "rejected" && (
                <div className="text-center text-lg font-medium text-red-600">
                  This payout was rejected
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
